import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Loader2, Pencil } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { useI18n } from '@/lib/i18n';
import { fetchUstawieniaAutomatu, zapiszUstawienie, type Ustawienie } from '@/lib/ustawienia-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  USTAWIENIA AUTOMATU — `SetupData` do przejrzenia i zmiany, jak w edytorze parametrów SPSConfig.
 *
 *  ⚠️⚠️ ZAMÓWIONE 23.09.2026: „chciałbym cały setupdata z możliwością edycji". Zakres i walidacja
 *  są z SPSConfig (serwis: `ustawienia/ustawienia-sps.ts`) — ekran nie ma własnych reguł poza tym,
 *  jak pokazać pole danego typu.
 *
 *  ⚠️ WYSZUKIWARKA, NIE ZAKŁADKI GRUP. Grupy z przedrostka opisu są drobne (na IKEA prawie
 *  sześćdziesiąt, z czego większość po kilka ustawień), więc służą za filtr i etykietę, a drogą
 *  do ustawienia jest wpisanie kawałka nazwy albo opisu.
 *
 *  ⚠️ „INNA NA…" PRZY KAŻDYM USTAWIENIU, które różni się od pozostałych automatów — na IKEA dwanaście
 *  automatów ma identyczne ustawienia poza logo, więc różnica jest tu sygnałem, a nie szumem.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function UstawieniaAutomatu({ groupId, vmid }: { groupId: string; vmid: number }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [szukaj, setSzukaj] = useState('');
  const [grupa, setGrupa] = useState('');
  const [tylkoRozne, setTylkoRozne] = useState(false);
  const [edycja, setEdycja] = useState<{ param: string; wartosc: string; wszystkie: boolean } | null>(null);
  const [zapisuje, setZapisuje] = useState(false);

  const dane = useQuery({
    queryKey: ['ustawienia', groupId, vmid],
    queryFn: () => fetchUstawieniaAutomatu(groupId, vmid),
  });

  const ustawienia = dane.data?.ustawienia ?? [];
  const automaty = dane.data?.automaty ?? [];
  const grupy = useMemo(
    () => [...new Set(ustawienia.map((u) => u.grupa).filter((g): g is string => g !== null))].sort((a, b) => a.localeCompare(b, 'pl')),
    [ustawienia],
  );
  const widoczne = useMemo(() => {
    const q = szukaj.trim().toLowerCase();
    return ustawienia.filter(
      (u) =>
        (grupa === '' || (grupa === '-' ? u.grupa === null : u.grupa === grupa)) &&
        (!tylkoRozne || u.inneNa.length > 0) &&
        (q === '' || [u.param, u.opis ?? '', u.wartosc ?? ''].some((x) => x.toLowerCase().includes(q))),
    );
  }, [ustawienia, szukaj, grupa, tylkoRozne]);

  const pokazWartosc = (u: Ustawienie, w: string | null): string => {
    if (w === null || w === '') return '—';
    if (u.typ === 'B') return w === '1' ? t('ust.tak') : w === '0' ? t('ust.nie') : w;
    return w;
  };

  /** Ile automatów obejmie „na wszystkich" — te, które parametr MAJĄ (starsza instancja: cała wyspa). */
  const ileZParametrem = (u: Ustawienie): number => u.automatow ?? automaty.length;

  const zapisz = async (u: Ustawienie): Promise<void> => {
    if (edycja === null) return;
    /** ⚠️ „Wszystkie" rozstrzyga serwer — patrz `zapiszUstawienie`. */
    const zakres = edycja.wszystkie || u.naWszystkich ? ({ wszystkie: true } as const) : { vmids: [vmid] };
    setZapisuje(true);
    try {
      const w = await zapiszUstawienie(groupId, u.param, edycja.wartosc, zakres);
      toast.success(t('ust.zakolejkowano').replace('{param}', u.param).replace('{n}', String(w.automatow)));
      setEdycja(null);
      await qc.invalidateQueries({ queryKey: ['ustawienia', groupId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setZapisuje(false);
    }
  };

  if (dane.isPending) return <Loader2 className="size-4 animate-spin text-muted-foreground" />;
  if (dane.isError) return <p className="text-sm text-[var(--destructive)]">{(dane.error as Error).message}</p>;

  const zapis = dane.data.zapis;

  return (
    <section className="space-y-3 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-sm font-medium">{t('ust.tytul')}</h2>
        <span className="text-xs text-muted-foreground">{t('ust.licznik').replace('{n}', String(ustawienia.length))}</span>
        {/* ⚠️ Ten sam zestaw z perspektywy całej wyspy — tam widać od razu, który automat odstaje. */}
        <Link to="/automaty?zakladka=ustawienia" className="ml-auto text-xs text-[var(--accent)] hover:underline">
          {t('ust.calaWyspa')}
        </Link>
      </div>

      {/*
        ⚠️⚠️ BRAMKA WERSJI WIDOCZNA ZANIM KTOŚ KLIKNIE — starszy agent zapisałby ustawienie i przy
        następnym odczycie zgłosił je jako usunięte, więc serwis i tak odmówi. Zdanie z listą baz
        mówi od razu, co zrobić, zamiast odmowy po każdej próbie.
      */}
      {!zapis.mozliwy && (
        <p className="text-xs text-[var(--warning)]">
          {t('ust.zapisBlokuje').replace('{wersja}', zapis.wymagana).replace('{wezly}', zapis.starsze.join(', '))}
        </p>
      )}

      {ustawienia.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('ust.brak').replace('{wersja}', zapis.wymagana)}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="h-8 w-72"
              placeholder={t('ust.szukaj')}
              value={szukaj}
              onChange={(e) => setSzukaj(e.target.value)}
            />
            <Select className="h-8 w-56" value={grupa} onChange={(e) => setGrupa(e.target.value)}>
              <option value="">{t('ust.wszystkieGrupy')}</option>
              {grupy.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
              <option value="-">{t('ust.bezGrupy')}</option>
            </Select>
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={tylkoRozne} onChange={(e) => setTylkoRozne(e.target.checked)} />
              {t('ust.tylkoRozne')}
            </label>
            <span className="text-xs text-muted-foreground">{widoczne.length}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <tbody>
                {widoczne.map((u) => {
                  const edytowany = edycja?.param === u.param;
                  return (
                    <tr key={u.param} className="border-t border-border align-top">
                      <td className="py-1.5 pr-3">
                        <div>{u.opis ?? u.param}</div>
                        <div className="font-mono text-[10px] text-muted-foreground">
                          {u.param}
                          {u.grupa !== null && <span className="ml-2 font-sans">{u.grupa}</span>}
                        </div>
                      </td>
                      <td className="w-72 py-1.5 pr-3">
                        {edytowany ? (
                          <div className="space-y-1.5">
                            {u.typ === 'B' ? (
                              <Select
                                className="h-7"
                                value={edycja.wartosc}
                                onChange={(e) => setEdycja({ ...edycja, wartosc: e.target.value })}
                              >
                                <option value="1">{t('ust.tak')}</option>
                                <option value="0">{t('ust.nie')}</option>
                              </Select>
                            ) : (
                              <Input
                                className="h-7 font-mono text-xs"
                                inputMode={u.typ === 'I' ? 'numeric' : undefined}
                                maxLength={255}
                                value={edycja.wartosc}
                                onChange={(e) => setEdycja({ ...edycja, wartosc: e.target.value })}
                              />
                            )}
                            {/* ⚠️ SPSConfig też pozwala dać tę samą wartość wszystkim automatom naraz. */}
                            {ileZParametrem(u) > 1 && (
                              <label className="flex items-center gap-1.5">
                                <input
                                  type="checkbox"
                                  checked={edycja.wszystkie || u.naWszystkich}
                                  disabled={u.naWszystkich}
                                  onChange={(e) => setEdycja({ ...edycja, wszystkie: e.target.checked })}
                                />
                                {t('ust.wszystkieAutomaty').replace('{n}', String(ileZParametrem(u)))}
                              </label>
                            )}
                            <div className="flex gap-1">
                              <Button size="sm" className="h-7" disabled={zapisuje} onClick={() => void zapisz(u)}>
                                {zapisuje && <Loader2 className="size-3 animate-spin" />}
                                {t('common.save')}
                              </Button>
                              <Button size="sm" variant="outline" className="h-7" disabled={zapisuje} onClick={() => setEdycja(null)}>
                                {t('common.cancel')}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <span className="flex items-center gap-1">
                            <span className={u.typ === 'D' || u.typ === 'F' ? 'break-all font-mono' : ''}>
                              {pokazWartosc(u, u.wartosc)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-6"
                              title={t('ust.zmien')}
                              aria-label={`${t('ust.zmien')} ${u.param}`}
                              disabled={!zapis.mozliwy}
                              onClick={() =>
                                setEdycja({
                                  param: u.param,
                                  wartosc: u.wartosc ?? (u.typ === 'B' ? '0' : ''),
                                  wszystkie: u.naWszystkich,
                                })
                              }
                            >
                              <Pencil className="size-3" />
                            </Button>
                          </span>
                        )}
                      </td>
                      <td className="py-1.5">
                        <span className="flex flex-wrap gap-1">
                          {u.wKolejce && (
                            <Badge variant="warning">
                              <Clock className="size-3" />
                              {t('ust.wKolejce')}
                            </Badge>
                          )}
                          {u.naWszystkich && <Badge variant="neutral">{t('ust.naWszystkich')}</Badge>}
                          {u.inneNa.length > 0 && (
                            <span className="text-[11px] text-muted-foreground">
                              {t('ust.inneNa').replace(
                                '{lista}',
                                u.inneNa.map((o) => `${o.vmid} (${pokazWartosc(u, o.wartosc)})`).join(', '),
                              )}
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
